/** @jsx jsx */
import { css, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';
import { useCallback } from 'react';

import { IconButton } from '@atlaskit/button/new';
import { Box } from '@atlaskit/primitives/compiled';
import SearchIcon from '@atlaskit/icon/core/search';
import CrossIcon from '@atlaskit/icon/core/cross';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks'))
			.fireContentMenuItemClickedAnalytics,
);

type ContentMenuTextFilterProps = {
	filterText: string;
	setFilterText: (filterText: string) => void;
	placeholder: string;
	isDisabled: boolean;
	actionSubjectId: string;
	itemId: string;
};

const textFieldStyles = css({
	borderColor: token('color.border'),
});

export const ContentMenuTextFilter = ({
	filterText,
	setFilterText,
	placeholder,
	isDisabled,
	actionSubjectId,
	itemId,
}: ContentMenuTextFilterProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, actionSubjectId, {
		componentType: 'fixedItem',
		itemId,
	});

	const setFilterFieldRef = useCallback((node: HTMLInputElement) => {
		if (node) {
			node.focus();
		}
	}, []);

	return (
		<Textfield
			ref={setFilterFieldRef}
			value={filterText}
			onChange={(e) => setFilterText(e.currentTarget.value)}
			onMouseDown={fireClickAnalytics}
			elemBeforeInput={
				<Box paddingInlineStart="space.100">
					<SearchIcon label="" color="currentColor" />
				</Box>
			}
			elemAfterInput={
				Boolean(filterText) && (
					<Box paddingInlineEnd="space.050">
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={CrossIcon}
							label={intl.formatMessage(i18n.clearFilter)}
							onClick={() => setFilterText('')}
						/>
					</Box>
				)
			}
			isCompact
			placeholder={placeholder}
			isDisabled={isDisabled}
			css={textFieldStyles}
			aria-label={placeholder}
		/>
	);
};

const i18n = defineMessages({
	clearFilter: {
		id: 'side-navigation.content-menu.text-filter.clear-filter',
		defaultMessage: 'Clear filter',
		description: 'Label for the clear filter button in the content menu text filter',
	},
});
